import React, { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import GLPdfExporter from "./GLPdfExporter";

const GLAssessment = ({
  role,
  lang,
  child,
  responses,
  setResponses,
  deepDiveResponses,
  setDeepDiveResponses,
  DOMAINS,
  SCALE,
  ROLES,
  activeDeepDive,
  setActiveDeepDive,
  domainStats,
  consistencyIndex,
  shouldDeepDive,
  deepDiveStats,
  DEEP_DIVE_DOMAINS,
  DEEP_DIVE_CTAS,
  deepDiveInsightIndex,
  deriveDeepDiveSignals,
  buildChartData,
  darkChartOptions,
  updateScore,
  updateDeepDive,
  hasAnyResponse,
  handlePrint,
  handleShare,
  navigate,
  handleSaveAssessment,
}) => {
  const roleLabel = {
    parent: { en: "Parent Assessment", hi: "अभिभावक मूल्यांकन" },
    educator: { en: "Educator Assessment", hi: "शिक्षक मूल्यांकन" },
    doctor: { en: "Doctor Assessment", hi: "डॉक्टर मूल्यांकन" },
    observer: { en: "Observer Assessment", hi: "अवलोकक मूल्यांकन" },
  }[role];

  const noData = !hasAnyResponse(role);
  const handleSave = () => handleSaveAssessment();
  const handlePrintReport = () => handlePrint();
  const handleShareReport = () => handleShare();
  const handleDownloadPDF = () => GLPdfExporter.download(role);

  function EmptyState({ message }) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center text-gray-200">
        <i className="fa-solid fa-chart-simple text-lg lg:text-2xl mb-3 opacity-30" />
        <p className="text-xs text-gray-200 leading-relaxed max-w-xs">
          {message}
        </p>
      </div>
    );
  }

  const doctorSummary = useMemo(() => {
    const lines = [];
    DOMAINS.forEach((d) => {
      const hits = ROLES.filter((r) => {
        const { total, max } = domainStats(r, d);
        return max && total / max >= 0.6;
      });
      if (hits.length >= 2)
        lines.push(
          `Consistent signals in ${d.title.en.toLowerCase()} across ${hits.join(" & ")}.`,
        );
    });
    return lines.length
      ? lines.join(" ") + " Non-diagnostic pattern summary only."
      : "No strong cross-setting patterns observed yet. Complete all assessments for a fuller picture.";
  }, [responses, deepDiveResponses]);

  const DeepDivePanel = ({ domain }) => {
    const questions = DEEP_DIVE_DOMAINS[domain.id]?.[lang] || [];
    const ddResp    = deepDiveResponses[domain.id] || [];
    const signals   = deriveDeepDiveSignals(domain.id, ddResp);
    const { total, max } = deepDiveStats(domain.id);

    return (
      <div className="mt-3 border border-cyan-800 rounded-xl p-4 bg-black space-y-3">
        <div>
          <h4 className="text-sm font-bold text-cyan-300">
            Deep Thinking — {domain.title[lang]}
          </h4>
          <p className="text-[10px] text-gray-200 mt-0.5">
            Reflective questions exploring why patterns occur. Non-diagnostic.
          </p>
        </div>

        {questions.map((q, qi) => (
          <div key={qi}>
            <p className="text-[11px] text-white mb-1.5 leading-relaxed">{q}</p>
            <div className="grid grid-cols-4 gap-1.5">
              {SCALE.map((v) => (
                <button
                  key={v}
                  onClick={() => updateDeepDive(domain.id, qi, v)}
                  className={`py-1.5 text-xs rounded-lg font-semibold transition ${
                    ddResp[qi] === v
                      ? "bg-cyan-400 text-black"
                      : "bg-gray-800 text-gray-100 hover:bg-gray-700"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-between text-[11px] pt-2 border-t border-gray-800">
          <span className="text-gray-200">
            Score: <span className="text-white font-semibold">{total} / {max}</span>
          </span>
          <span className="text-cyan-400 font-medium">
            {deepDiveInsightIndex(domain.id)}
          </span>
        </div>

        <DeepDiveCTAs signals={signals} />
      </div>
    );
  };

  

  return (
    <div className="flex justify-center py-2">
      <div
        id={`assessment-${role}`}
        className="w-full max-w-2xl space-y-4 text-white"
      >
        {/* Title */}
        <div className="text-center">
          <h2 className="text-base font-bold text-cyan-300 uppercase tracking-wide">
            {roleLabel[lang]}
          </h2>
          {child.name && (
            <p className="text-xs text-gray-200 mt-0.5">
              {lang === "hi" ? "बच्चा:" : "Child:"}{" "}
              <span className="text-gray-100">{child.name}</span>
              {child.age && `, ${child.age}`}
              {child.grade &&
                ` · ${lang === "hi" ? "कक्षा" : "Grade"} ${child.grade}`}
            </p>
          )}
        </div>

        {/* Chart */}
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-3">
          {noData ? (
            <EmptyState
              message={
                lang === "hi"
                  ? "मूल्यांकन शुरू करने के लिए नीचे प्रश्नों के उत्तर दें।"
                  : "Answer questions below to see scores appear here."
              }
            />
          ) : (
            <div style={{ height: 200 }}>
              <Bar
                data={buildChartData(role)}
                options={darkChartOptions({
                  plugins: { legend: { labels: { color: "#d1d5db" } } },
                })}
              />
            </div>
          )}
        </div>

        {/* Domain cards */}
        <div className="space-y-4">
          {DOMAINS.map((domain) => {
            const { total, max, answered, qCount } = domainStats(role, domain);
            const pct = max ? Math.round((total / max) * 100) : 0;
            const showDD = shouldDeepDive(role, domain);
            const ci = consistencyIndex(domain.id);

            return (
              <div
                key={domain.id}
                className="border border-gray-800 rounded-xl p-4 bg-gray-950"
              >
                {/* Domain header */}
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xs font-semibold text-cyan-300 leading-snug flex-1 pr-2">
                    {domain.title[lang]}
                  </h3>
                  {answered > 0 && (
                    <span
                      className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${
                        pct >= 60
                          ? "bg-red-900 text-red-300"
                          : pct >= 35
                            ? "bg-yellow-900 text-yellow-300"
                            : "bg-green-900 text-green-300"
                      }`}
                    >
                      {pct}%
                    </span>
                  )}
                </div>

                {/* Questions */}
                {domain.questions[role]?.[lang]?.map((q, qi) => (
                  <div key={qi} className="mb-3">
                    <p className="text-xs text-gray-100 mb-1.5 leading-relaxed">
                      {qi + 1}. {q}
                    </p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {SCALE.map((v) => {
                        const isSelected =
                          responses[role]?.[domain.id]?.[qi] === v;
                        return (
                          <button
                            key={v}
                            onClick={() => updateScore(role, domain.id, qi, v)}
                            className={`w-fit px-2 py-1 text-xs rounded-lg font-semibold transition ${
                              isSelected
                                ? "bg-cyan-500 text-black"
                                : "bg-gray-800 text-gray-100 hover:bg-gray-700"
                            }`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Score footer */}
                <div className="flex justify-between items-center text-xs mt-2 pt-2 border-t border-gray-800">
                  <span className="text-gray-200">
                    {lang === "hi" ? "स्कोर" : "Score"}:{" "}
                    <span className="text-white font-semibold">
                      {total} / {max}
                    </span>
                    <span className="text-gray-300 ml-1">
                      ({answered}/{qCount} answered)
                    </span>
                  </span>
                  <span className="text-gray-200">
                    {lang === "hi" ? "सुसंगतता" : "Consistency"}: {ci}
                  </span>
                </div>

                {/* Deep Dive trigger */}
                {showDD && (
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <button
                      onClick={() =>
                        setActiveDeepDive(
                          activeDeepDive === domain.id ? null : domain.id,
                        )
                      }
                      className="flex w-fit px-2 py-1 items-center gap-1.5 text-xs font-semibold text-orange-300 hover:text-orange-200 transition"
                    >
                      <i className="fa-solid fa-magnifying-glass text-xs" />
                      {activeDeepDive === domain.id
                        ? lang === "hi"
                          ? "बंद करें"
                          : "Close Deep Dive"
                        : lang === "hi"
                          ? "गहन विचार"
                          : "Deep Dive"}
                    </button>
                    {activeDeepDive === domain.id && (
                      <DeepDivePanel domain={domain} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Doctor summary */}
        {role === "doctor" && (
          <div className="border border-cyan-900 rounded-xl p-4 bg-gray-950">
            <h3 className="text-xs font-semibold text-cyan-400 mb-2">
              {lang === "hi"
                ? "क्लिनिकल पैटर्न सारांश (गैर-निदानात्मक)"
                : "Clinical Pattern Summary (Non-Diagnostic)"}
            </h3>
            <p className="text-xs text-gray-100 leading-relaxed">
              {doctorSummary}
            </p>
          </div>
        )}

        {/* Action bar */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-800">
          {[
            {
              icon: "🤖",
              label: lang === "hi" ? "एआई जांच" : "Check with AI",
              disabled: true,
              action: () => navigate("/agenticsim"),
            },
            {
              icon: "💾",
              label: lang === "hi" ? "सहेजें" : "Save",
              disabled: false,
              action: handleSave,
            },
            {
              icon: "🖨",
              label: lang === "hi" ? "छापें" : "Print",
              disabled: false,
              action: handlePrintReport,
            },
            {
              icon: "📄",
              label: lang === "hi" ? "PDF" : "Download PDF",
              disabled: false,
              action: handleDownloadPDF,
            },
            {
              icon: "📤",
              label: lang === "hi" ? "साझा करें" : "Share",
              disabled: false,
              action: handleShareReport,
            },
            {
              icon: "👩‍⚕️",
              label: lang === "hi" ? "विशेषज्ञ" : "Contact Expert",
              disabled: false,
              action: () => navigate("/think5whys-counsellors"),
            },
          ].map((btn, i) => (
            <button
              key={i}
              disabled={btn.disabled}
              onClick={btn.action}
              title={btn.disabled ? "Coming soon" : btn.label}
              className="flex items-center justify-center gap-1 w-fit px-2 py-1 rounded-lg text-xs bg-teal-900 text-teal-300 disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:bg-teal-800 transition"
            >
              <span>{btn.icon}</span>
              <span className="truncate">{btn.label}</span>
            </button>
          ))}
        </div>

        {/* Coming soon note */}
        <p className="text-center text-xs text-gray-300">
          Save, PDF, Share and AI actions unlock after login & full release.
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 — renderAssessment (parent / educator / doctor)
// ═══════════════════════════════════════════════════════════════════════════

const DeepDiveCTAs = ({ signals }) => {
  if (!Object.values(signals).some(Boolean)) return null;
  return (
    <div className="mt-3 space-y-2">
      {Object.entries(signals).map(([key, active]) => {
        if (!active || !DEEP_DIVE_CTAS[key]) return null;
        const cta = DEEP_DIVE_CTAS[key];
        return (
          <div
            key={key}
            className="border border-gray-700 rounded-lg p-3 bg-gray-900"
          >
            <div className="text-xs font-semibold text-cyan-300 mb-0.5">
              {typeof cta.label === "object" ? cta.label[lang] : cta.label}
            </div>
            <div className="text-xs text-gray-200 leading-relaxed">
              {typeof cta.action === "object" ? cta.action[lang] : cta.action}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GLAssessment;
